import { Badge, Button, Card, CopyableValue, EmptyState, Stat } from '../ui/primitives';

/**
 * The DMARC picture for one sending domain.
 *
 * Deliberately leads with the effective policy rather than the record text:
 * the common failure is a domain that looks protected on a registrar page
 * while receivers are applying something else entirely - an inherited
 * subdomain policy, a duplicate record that voids both, or a reject policy
 * the domain's own authentication cannot satisfy. Each finding carries the
 * remedy next to it, because an alert that only names a problem gets ignored.
 */

export interface DmarcFinding {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  detail: string;
  remedy: string | null;
}

export interface DmarcAnalysis {
  domain: string;
  organizationalDomain: string;
  checkedAt: string;
  present: boolean;
  inherited: boolean;
  source: string | null;
  record: { raw: string; pct: number; adkim: string; aspf: string } | null;
  effectivePolicy: 'none' | 'quarantine' | 'reject';
  enforcement: 'none' | 'partial' | 'quarantine' | 'reject';
  alignmentRisk: 'low' | 'medium' | 'high';
  findings: DmarcFinding[];
  score: number;
  grade: string;
  summary: string;
}

const POLICY_TONE: Record<DmarcAnalysis['effectivePolicy'], 'good' | 'warn' | 'bad'> = {
  reject: 'good',
  quarantine: 'warn',
  none: 'bad',
};

const SEVERITY_TONE: Record<DmarcFinding['severity'], 'bad' | 'warn' | 'info'> = {
  critical: 'bad',
  warning: 'warn',
  info: 'info',
};

const RISK_TONE: Record<DmarcAnalysis['alignmentRisk'], 'good' | 'warn' | 'bad'> = {
  low: 'good',
  medium: 'warn',
  high: 'bad',
};

export function DmarcPanel({
  analysis,
  onRescan,
  rescanning,
}: {
  analysis: DmarcAnalysis | null;
  onRescan?: () => void;
  rescanning?: boolean;
}) {
  const action = onRescan ? (
    <Button variant="secondary" onClick={onRescan} disabled={rescanning}>
      {rescanning ? 'Scanning...' : 'Scan DMARC now'}
    </Button>
  ) : undefined;

  if (!analysis) {
    return (
      <Card
        title="DMARC"
        subtitle="Scanned every morning, and whenever you press the button."
        actions={action}
      >
        <EmptyState
          title="Not scanned yet"
          body="The daily scan runs at 05:30. Press Scan DMARC now to check this domain immediately."
        />
      </Card>
    );
  }

  const criticals = analysis.findings.filter((f) => f.severity === 'critical');
  const warnings = analysis.findings.filter((f) => f.severity === 'warning');
  const infos = analysis.findings.filter((f) => f.severity === 'info');

  return (
    <Card
      title="DMARC"
      subtitle={analysis.summary}
      actions={action}
    >
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat
          label="Effective policy"
          value={'p=' + analysis.effectivePolicy}
          tone={POLICY_TONE[analysis.effectivePolicy]}
          hint={
            analysis.enforcement === 'partial'
              ? 'Applied to part of your mail only'
              : analysis.inherited
                ? 'Inherited from ' + analysis.organizationalDomain
                : 'Published on this domain'
          }
        />
        <Stat label="Score" value={analysis.score + '/100'} hint={'Grade ' + analysis.grade} />
        <Stat
          label="Alignment risk"
          value={analysis.alignmentRisk}
          tone={RISK_TONE[analysis.alignmentRisk]}
          hint="Chance your own mail fails this policy"
        />
        <Stat
          label="Problems"
          value={String(criticals.length + warnings.length)}
          tone={criticals.length ? 'bad' : warnings.length ? 'warn' : 'good'}
          hint={criticals.length + ' critical, ' + warnings.length + ' to improve'}
        />
      </div>

      {analysis.record && (
        <div className="mt-5">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-xs font-medium text-ink-700">Record in effect</span>
            {analysis.source && (
              <span className="font-mono text-[11px] text-ink-400">{analysis.source}</span>
            )}
            {analysis.inherited && <Badge tone="info">inherited</Badge>}
          </div>
          <CopyableValue value={analysis.record.raw} />
        </div>
      )}

      {analysis.findings.length === 0 ? (
        <p className="mt-5 text-xs text-emerald-700">
          Nothing to fix. This domain is publishing an enforced policy with reporting in place.
        </p>
      ) : (
        <ul className="mt-5 space-y-3">
          {[...criticals, ...warnings, ...infos].map((finding, index) => (
            <li
              key={finding.id + ':' + index}
              className="rounded-lg border border-ink-200 bg-white p-3"
            >
              <div className="flex items-start gap-2">
                <Badge tone={SEVERITY_TONE[finding.severity]}>{finding.severity}</Badge>
                <span className="text-sm font-medium leading-5 text-ink-900">{finding.title}</span>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-ink-600">{finding.detail}</p>
              {finding.remedy && (
                <p className="mt-1.5 text-xs leading-relaxed text-ink-900">
                  <span className="font-medium">Fix:</span> {finding.remedy}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
